import type { Language } from './i18n';

type Choice = { id: string; title: string; detail: string };
type BuilderCopy = {
  steps: [string, string, string]; back: string; next: string; start: string;
  detailsTitle: string; reviewTitle: string; preview: string; draft: string;
  goal: string; goals: Choice[]; size: string; sizes: Choice[];
  tools: string; toolOptions: string[]; timing: string; timings: string[];
  optional: string; contactNote: string; selected: string; progress: string;
  flow: [string, string, string]; light: string; dark: string; theme: string; top: string;
  otherTools: string; otherToolsPlaceholder: string; notes: string; notesPlaceholder: string;
  contactTitle: string; send: string; sentTitle: string; sentText: string; newProject: string;
};

export const builderCopy: Record<Language, BuilderCopy> = {
  pl: {
    steps: ['Twój pomysł', 'Dopasuj zakres', 'Podsumowanie'], back: 'Wstecz', next: 'Dalej', start: 'Zaplanuj projekt',
    detailsTitle: 'Kilka szczegółów. Lepszy punkt wyjścia.', reviewTitle: 'Twój projekt zaczyna nabierać kształtu.',
    preview: 'Szkic projektu', draft: 'Wstępny zakres', goal: 'Od czego zaczynamy?',
    goals: [
      { id: 'new', title: 'Nowy projekt', detail: 'Mam pomysł, potrzebuję rozwiązania.' },
      { id: 'improve', title: 'Ulepszenie', detail: 'Coś już działa, ale może lepiej.' },
      { id: 'connect', title: 'Połączenie narzędzi', detail: 'Chcę uporządkować obecny proces.' }
    ],
    size: 'Jak duży jest pierwszy krok?', sizes: [
      { id: 'focused', title: 'Jeden konkretny problem', detail: 'Mały zakres, skupiony na jednym zadaniu.' },
      { id: 'standard', title: 'Kilka połączonych funkcji', detail: 'Ekrany, dane i jeden spójny proces.' },
      { id: 'advanced', title: 'Większy system', detail: 'Role, integracje i kilka etapów pracy.' }
    ],
    tools: 'Co ma ze sobą współpracować?', toolOptions: ['E-mail', 'Formularze', 'CRM', 'Kalendarz'],
    timing: 'Kiedy chcesz zacząć?', timings: ['Elastycznie', 'W ciągu miesiąca', 'W ciągu 3 miesięcy'],
    optional: 'Opcjonalnie', contactNote: 'To nie jest zamówienie. Najpierw wspólnie potwierdzimy zakres, koszt i termin.',
    selected: 'Wybrane obszary', progress: 'Etap', flow: ['Informacja', 'Twój proces', 'Gotowe działanie'],
    light: 'Jasny', dark: 'Ciemny', theme: 'Motyw strony', top: 'Na górę',
    otherTools: 'Inne narzędzia', otherToolsPlaceholder: 'np. Notion, Excel, WhatsApp',
    notes: 'Twój opis projektu', notesPlaceholder: 'Opisz swój pomysł, problem lub to, co chcesz usprawnić.',
    contactTitle: 'Gdzie wysłać odpowiedź?', send: 'Wyślij zapytanie o wycenę',
    sentTitle: 'Dziękuję, zapytanie zostało wysłane.', sentText: 'Mam Twój opis i wybrany zakres. Odpowiem na podany adres e-mail.', newProject: 'Zaplanuj kolejny projekt'
  },
  en: {
    steps: ['Your idea', 'Fine-tune', 'Your brief'], back: 'Back', next: 'Continue', start: 'Build your brief',
    detailsTitle: 'A few details. A better starting point.', reviewTitle: 'Your project is taking shape.',
    preview: 'Project sketch', draft: 'Initial scope', goal: 'Where do we start?',
    goals: [
      { id: 'new', title: 'New project', detail: 'An idea that needs a working solution.' },
      { id: 'improve', title: 'Improve something', detail: 'It works, but could work better.' },
      { id: 'connect', title: 'Connect tools', detail: 'Bring an existing process together.' }
    ],
    size: 'How big is the first step?', sizes: [
      { id: 'focused', title: 'One focused problem', detail: 'A small scope built around one task.' },
      { id: 'standard', title: 'Connected features', detail: 'Screens, data and a coherent workflow.' },
      { id: 'advanced', title: 'A larger system', detail: 'Roles, integrations and multiple stages.' }
    ],
    tools: 'What needs to work together?', toolOptions: ['Email', 'Forms', 'CRM', 'Calendar'],
    timing: 'When would you like to start?', timings: ['Flexible', 'Within a month', 'Within 3 months'],
    optional: 'Optional', contactNote: 'This is not an order. We will confirm the scope, cost and timeline together first.',
    selected: 'Selected areas', progress: 'Step', flow: ['Information', 'Your workflow', 'Action ready'],
    light: 'Light', dark: 'Dark', theme: 'Page theme', top: 'Back to top',
    otherTools: 'Other tools', otherToolsPlaceholder: 'e.g. Notion, Excel, WhatsApp',
    notes: 'Your project description', notesPlaceholder: 'Describe your idea, the problem or what you would like to improve.',
    contactTitle: 'Where should I reply?', send: 'Send quote request',
    sentTitle: 'Thank you. Your request has been sent.', sentText: 'I have your description and selected scope. I will reply to the email address you provided.', newProject: 'Plan another project'
  },
  uk: {
    steps: ['Ваша ідея', 'Деталі', 'Підсумок'], back: 'Назад', next: 'Далі', start: 'Спланувати проєкт',
    detailsTitle: 'Кілька деталей для кращого початку.', reviewTitle: 'Ваш проєкт набуває форми.',
    preview: 'Ескіз проєкту', draft: 'Попередній обсяг', goal: 'Із чого починаємо?',
    goals: [
      { id: 'new', title: 'Новий проєкт', detail: 'Є ідея, потрібне робоче рішення.' },
      { id: 'improve', title: 'Покращення', detail: 'Уже працює, але може краще.' },
      { id: 'connect', title: 'Поєднання інструментів', detail: 'Хочу впорядкувати наявний процес.' }
    ],
    size: 'Яким буде перший крок?', sizes: [
      { id: 'focused', title: 'Одна конкретна задача', detail: 'Невеликий обсяг з чіткою метою.' },
      { id: 'standard', title: 'Пов’язані функції', detail: 'Екрани, дані та єдиний процес.' },
      { id: 'advanced', title: 'Більша система', detail: 'Ролі, інтеграції та етапи роботи.' }
    ],
    tools: 'Що має працювати разом?', toolOptions: ['Електронна пошта', 'Форми', 'CRM', 'Календар'],
    timing: 'Коли хочете почати?', timings: ['Гнучко', 'Протягом місяця', 'Протягом 3 місяців'],
    optional: 'Необов’язково', contactNote: 'Це не замовлення. Спочатку разом погодимо обсяг, вартість і термін.',
    selected: 'Обрані напрями', progress: 'Етап', flow: ['Інформація', 'Ваш процес', 'Готова дія'],
    light: 'Світла', dark: 'Темна', theme: 'Тема сторінки', top: 'Нагору',
    otherTools: 'Інші інструменти', otherToolsPlaceholder: 'наприклад, Notion, Excel, WhatsApp',
    notes: 'Ваш опис проєкту', notesPlaceholder: 'Опишіть ідею, проблему або те, що хочете покращити.',
    contactTitle: 'Куди надіслати відповідь?', send: 'Надіслати запит на оцінку',
    sentTitle: 'Дякую, запит надіслано.', sentText: 'Я отримав ваш опис та обрані напрями. Відповім на вказану електронну адресу.', newProject: 'Спланувати інший проєкт'
  },
  ru: {
    steps: ['Ваша идея', 'Детали', 'Итог'], back: 'Назад', next: 'Далее', start: 'Спланировать проект',
    detailsTitle: 'Несколько деталей для лучшего старта.', reviewTitle: 'Ваш проект обретает форму.',
    preview: 'Эскиз проекта', draft: 'Предварительный объём', goal: 'С чего начинаем?',
    goals: [
      { id: 'new', title: 'Новый проект', detail: 'Есть идея, нужно рабочее решение.' },
      { id: 'improve', title: 'Улучшение', detail: 'Уже работает, но может лучше.' },
      { id: 'connect', title: 'Связать инструменты', detail: 'Хочу упорядочить текущий процесс.' }
    ],
    size: 'Каким будет первый шаг?', sizes: [
      { id: 'focused', title: 'Одна конкретная задача', detail: 'Небольшой объём с чёткой целью.' },
      { id: 'standard', title: 'Связанные функции', detail: 'Экраны, данные и единый процесс.' },
      { id: 'advanced', title: 'Большая система', detail: 'Роли, интеграции и этапы работы.' }
    ],
    tools: 'Что должно работать вместе?', toolOptions: ['Электронная почта', 'Формы', 'CRM', 'Календарь'],
    timing: 'Когда хотите начать?', timings: ['Гибко', 'В течение месяца', 'В течение 3 месяцев'],
    optional: 'Необязательно', contactNote: 'Это не заказ. Сначала вместе согласуем объём, стоимость и срок.',
    selected: 'Выбранные направления', progress: 'Этап', flow: ['Информация', 'Ваш процесс', 'Готовое действие'],
    light: 'Светлая', dark: 'Тёмная', theme: 'Тема страницы', top: 'Наверх',
    otherTools: 'Другие инструменты', otherToolsPlaceholder: 'например, Notion, Excel, WhatsApp',
    notes: 'Ваше описание проекта', notesPlaceholder: 'Опишите идею, проблему или то, что хотите улучшить.',
    contactTitle: 'Куда отправить ответ?', send: 'Отправить запрос на оценку',
    sentTitle: 'Спасибо, запрос отправлен.', sentText: 'Я получил ваше описание и выбранные направления. Отвечу на указанный адрес электронной почты.', newProject: 'Спланировать другой проект'
  }
};
